import { addItemToCart } from '@umbrella/cart-service';

/**
 * Product Card Custom Element
 *
 * - Quick-buy button either adds a single-variant product straight to the bag,
 *   or (for multi-variant products) opens an inline "Select your size" overlay.
 * - Picking a size sets the variant and enables "Add to cart".
 * - Add to cart posts through the theme's AJAX cart (cart-service), so the toast
 *   + quick-cart react as usual. Falls back to a normal form POST without JS.
 */
class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.submitButton = null;
    this.variantInput = null;
    this.panel = null;
    this.openButton = null;
    this.sizeButtons = [];
    this.isSubmitting = false;

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleSwatchOver = this.handleSwatchOver.bind(this);
    this.restoreSwatchPreview = this.restoreSwatchPreview.bind(this);
  }

  connectedCallback() {
    this.bindSwatches();

    this.form = this.querySelector('form[action*="/cart/add"]');
    if (!this.form) return;

    this.submitButton = this.form.querySelector('button[type="submit"]');
    this.variantInput = this.form.querySelector('input[name="id"]');

    this.panel = this.querySelector('[data-qb-panel]');
    // A card can have several triggers (e.g. image + title); focus returns to the visible one.
    this.openButton = this.querySelector('[data-qb-open]:not([aria-hidden="true"])') || this.querySelector('[data-qb-open]');
    this.sizeButtons = Array.from(this.querySelectorAll('[data-qb-size]'));

    this.form.addEventListener('submit', this.handleSubmit);
    this.addEventListener('click', this.handleClick);
    if (this.panel) this.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    if (this.form) this.form.removeEventListener('submit', this.handleSubmit);
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('click', this.handleOutsideClick);
    if (this.swatchRow) {
      this.swatchRow.removeEventListener('mouseover', this.handleSwatchOver);
      this.swatchRow.removeEventListener('mouseleave', this.restoreSwatchPreview);
    }
  }

  /* ---- Colour swatches (snippets/product-card-swatches.liquid) ----
     Hovering a dot previews that colour on the card; leaving the row puts the
     card back. Clicking a dot is a plain link to that colour's product. */
  bindSwatches() {
    this.swatchRow = this.querySelector('[data-pc-swatches]');
    if (!this.swatchRow) return;

    this.swatchImage = this.querySelector('[data-pc-media] img');
    this.swatchLinks = Array.from(this.querySelectorAll('[data-pc-link]'));
    this.swatchTitle = this.querySelector('[data-pc-title]');
    this.swatchPrice = this.querySelector('[data-pc-price]');
    this.swatchOriginal = {
      src: this.swatchImage ? this.swatchImage.getAttribute('src') : null,
      srcset: this.swatchImage ? this.swatchImage.getAttribute('srcset') : null,
      hrefs: this.swatchLinks.map((link) => link.getAttribute('href')),
      title: this.swatchTitle ? this.swatchTitle.textContent : null,
      price: this.swatchPrice ? this.swatchPrice.innerHTML : null,
    };

    this.swatchRow.addEventListener('mouseover', this.handleSwatchOver);
    this.swatchRow.addEventListener('mouseleave', this.restoreSwatchPreview);
  }

  handleSwatchOver(event) {
    const swatch = event.target.closest('[data-pc-swatch]');
    if (!swatch || swatch === this.activeSwatch) return;
    this.activeSwatch = swatch;

    const { src, srcset, url, title, price, comparePrice } = swatch.dataset;
    if (this.swatchImage && src) {
      if (srcset) this.swatchImage.setAttribute('srcset', srcset);
      this.swatchImage.setAttribute('src', src);
    }
    this.swatchLinks.forEach((link) => link.setAttribute('href', url));
    if (this.swatchTitle && title) this.swatchTitle.textContent = title;
    if (this.swatchPrice && price) this.swatchPrice.replaceChildren(...this.priceNodes(price, comparePrice));
  }

  restoreSwatchPreview() {
    if (!this.activeSwatch) return;
    this.activeSwatch = null;

    const original = this.swatchOriginal;
    if (this.swatchImage && original.src) {
      if (original.srcset) this.swatchImage.setAttribute('srcset', original.srcset);
      this.swatchImage.setAttribute('src', original.src);
    }
    this.swatchLinks.forEach((link, i) => link.setAttribute('href', original.hrefs[i]));
    if (this.swatchTitle && original.title !== null) this.swatchTitle.textContent = original.title;
    if (this.swatchPrice && original.price !== null) this.swatchPrice.innerHTML = original.price;
  }

  // Mirrors the price markup in snippets/product-card.liquid.
  priceNodes(price, comparePrice) {
    const current = document.createElement('span');
    current.className = comparePrice ? 'text-[#b23a32]' : 'text-black';
    current.textContent = price;
    if (!comparePrice) return [current];
    const compare = document.createElement('span');
    compare.className = 'text-black line-through';
    compare.textContent = comparePrice;
    return [current, compare];
  }

  handleClick(event) {
    if (event.target.closest('[data-qb-open]')) {
      event.preventDefault();
      this.openPanel();
      return;
    }
    if (event.target.closest('[data-qb-close]')) {
      event.preventDefault();
      this.closePanel();
      return;
    }
    const sizeButton = event.target.closest('[data-qb-size]');
    if (sizeButton && !sizeButton.disabled) {
      event.preventDefault();
      this.selectSize(sizeButton);
    }
  }

  // Clicking anywhere outside the open panel tucks it away (without pulling focus back).
  handleOutsideClick(event) {
    if (this.panel && !this.panel.hidden && !this.contains(event.target)) this.closePanel(false);
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.panel && !this.panel.hidden) {
      event.preventDefault();
      this.closePanel();
    }
  }

  openPanel() {
    if (!this.panel) return;
    this.panel.hidden = false;
    if (this.openButton) this.openButton.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this.handleOutsideClick);
    const firstEnabled = this.sizeButtons.find((btn) => !btn.disabled);
    // preventScroll: the panel/trigger live in an overflow-clipped card, and the
    // trigger sits off the bottom corner — a default focus() would scroll it into
    // view and jump the page.
    if (firstEnabled) firstEnabled.focus({ preventScroll: true });
  }

  closePanel(restoreFocus = true) {
    if (!this.panel) return;
    this.panel.hidden = true;
    document.removeEventListener('click', this.handleOutsideClick);
    if (this.openButton) {
      this.openButton.setAttribute('aria-expanded', 'false');
      if (restoreFocus) this.openButton.focus({ preventScroll: true });
    }
  }

  selectSize(button) {
    this.sizeButtons.forEach((btn) => btn.setAttribute('aria-pressed', 'false'));
    button.setAttribute('aria-pressed', 'true');

    if (this.variantInput) this.variantInput.value = button.dataset.variantId || '';
    if (this.submitButton) this.submitButton.disabled = false;
  }

  async handleSubmit(event) {
    event.preventDefault();

    if (this.isSubmitting || !this.form || !this.variantInput) return;

    const variantId = this.variantInput.value;
    if (!variantId) return;

    const quantityInput = this.form.querySelector('input[name="quantity"]');
    const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

    this.isSubmitting = true;

    const btnDisabledText = this.submitButton && this.submitButton.querySelector('[disabled-text]');
    const originalButtonText = btnDisabledText ? btnDisabledText.textContent : null;
    if (this.submitButton) this.submitButton.disabled = true;
    const loadingText = window.umbrellaConfig && window.umbrellaConfig.addToCart && window.umbrellaConfig.addToCart.submit;
    if (btnDisabledText && loadingText) btnDisabledText.textContent = loadingText;

    try {
      await addItemToCart(variantId, quantity, new FormData(this.form));
      // Cart drawer / toast take over from here; tuck the overlay away.
      this.closePanel();
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert((error && error.message) || 'Failed to add item to cart. Please try again.');
    } finally {
      this.isSubmitting = false;
      if (this.submitButton) {
        if (btnDisabledText && originalButtonText) btnDisabledText.textContent = originalButtonText;
        this.submitButton.disabled = false;
      }
    }
  }
}

customElements.define('product-card', ProductCard);
